// The file list row: which columns show at which panel width, and how the Modified column reads
// (app#328).
//
// ⚠⚠ MODIFIED IS NEVER HIDDEN. It was hidden under 500 px, and the file drawer is narrower than
// that on most screens, so most readers never saw when a file changed. On a narrow panel SIZE
// goes first: a reader who looks for a file asks "which one is new" more often than "which one is
// big", and the size is one click away in the viewer.
//
// ⚠ The rule is CSS (a container query on the list), so it is kept here as a plain object that
// `FileList.tsx` spreads into `sx` and that `fileList.test.ts` reads. A test of a copy of the rule
// would pass on a component that no longer uses it.
import type { Theme } from '@mui/material/styles'

// Under this container width the Size column is hidden. Modified and the row menu always show.
export const SIZE_MIN_WIDTH_PX = 420

// The list is the query container. Without `containerType` the `@container` rule below never
// matches, and Size never shows at any width.
export const fileListSx = { containerType: 'inline-size' } as const

export const fileRowSx = {
  gap: 1,
  paddingY: 0,
  minHeight: (theme: Theme) => theme.spacing(5.5),
  '& .row-date': { display: 'block', flexShrink: 0, whiteSpace: 'nowrap', textAlign: 'right' },
  '& .row-size': { display: 'none', width: 56, flexShrink: 0, textAlign: 'right' },
  '& .row-menu': { display: 'flex', alignItems: 'center' },
  [`@container (min-width: ${SIZE_MIN_WIDTH_PX}px)`]: {
    '& .row-size': { display: 'block' },
  },
} as const

const MINUTE_MS = 60_000

const time = (d: Date, locale?: string): string =>
  d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })

const startOfDay = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()

// The Modified cell: "Just now", "3 min ago", "Today 09:14", "Yesterday 14:02", "Sep 12",
// "Sep 12, 2025".
//
// ⚠ It is NOT `relativeTime` in `general.ts`. That one is DAY-granular ("today", "5 days ago"),
// which is right for an invoice and wrong for a file a reader saved a minute ago.
//
// ⚠ The reader's OWN zone. A file modification is an instant, and "when was that for me" is the
// question (the same reasoning as `freshnessLabel`).
//
// ⚠ A time in the future is a clock difference between the node and this browser. Up to one
// minute ahead reads as "Just now"; more than that gets the calendar form, never a negative age.
//
// ⚠ Returns '' for a missing or unparseable stamp, so the cell prints nothing rather than
// "Invalid Date".
//
// `locale` is for the test only. The component leaves it out, so the reader's locale applies.
export const modifiedLabel = (
  input: string | null | undefined,
  now: number = Date.now(),
  locale?: string,
): string => {
  if (!input) return ''
  const d = new Date(input)
  const t = d.getTime()
  if (Number.isNaN(t)) return ''
  const minutes = Math.floor((now - t) / MINUTE_MS)
  if (minutes >= -1 && minutes < 1) return 'Just now'
  if (minutes >= 1 && minutes < 60) return `${minutes} min ago`
  const nowDate = new Date(now)
  const today = startOfDay(nowDate)
  const day = startOfDay(d)
  if (day === today) return `Today ${time(d, locale)}`
  const yesterday = startOfDay(new Date(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate() - 1))
  if (day === yesterday) return `Yesterday ${time(d, locale)}`
  const sameYear = d.getFullYear() === nowDate.getFullYear()
  return d.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
}

// The hover text on the Modified cell: the exact date and time, with seconds and the zone.
// Returns '' for a missing or unparseable stamp. `locale` is for the test only.
export const modifiedTitle = (input: string | null | undefined, locale?: string): string => {
  if (!input) return ''
  const d = new Date(input)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    timeZoneName: 'short',
  })
}
